import { useLanguage } from '../context/LanguageContext';
import { MapPin, Train, Bus, Navigation, Mountain } from 'lucide-react';

const locationTranslations = {
  title: {
    fr: 'Localisation', en: 'Location', es: 'Ubicación', de: 'Standort', it: 'Posizione', nl: 'Locatie', ru: 'Местоположение', ar: 'الموقع', zh: '位置', ja: '所在地', ko: '위치', hi: 'स्थान'
  },
  address: {
    fr: 'Adresse', en: 'Address', es: 'Dirección', de: 'Adresse', it: 'Indirizzo', nl: 'Adres', ru: 'Адрес', ar: 'العنوان', zh: '地址', ja: '住所', ko: '주소', hi: 'पता'
  },
  trainStation: {
    fr: 'Gare SNCF Eze-sur-Mer', en: 'Eze-sur-Mer Train Station', es: 'Estación de tren Eze-sur-Mer', de: 'Bahnhof Eze-sur-Mer', it: 'Stazione Eze-sur-Mer', nl: 'Station Eze-sur-Mer', ru: 'Станция Eze-sur-Mer', ar: 'محطة قطار إيز سور مير', zh: '滨海埃兹火车站', ja: 'エズ・シュル・メール駅', ko: '에즈쉬르메르 기차역', hi: 'एज़-सुर-मेर रेलवे स्टेशन'
  },
  busStop: {
    fr: 'Arrêt de bus Eze Village', en: 'Eze Village Bus Stop', es: 'Parada de autobús Eze Village', de: 'Bushaltestelle Eze Village', it: 'Fermata bus Eze Village', nl: 'Bushalte Eze Village', ru: 'Автобусная остановка Eze Village', ar: 'محطة حافلات قرية إيز', zh: '埃兹村巴士站', ja: 'エズ村バス停', ko: '에즈 빌리지 버스 정류장', hi: 'एज़ विलेज बस स्टॉप'
  },
  distanceTo: {
    fr: 'Distance de', en: 'Distance to', es: 'Distancia a', de: 'Entfernung nach', it: 'Distanza da', nl: 'Afstand tot', ru: 'Расстояние до', ar: 'المسافة إلى', zh: '距离', ja: '距離', ko: '거리', hi: 'दूरी'
  },
  nietzschePath: {
    fr: 'Chemin Frédéric Nietzsche', en: 'Nietzsche Hiking Trail', es: 'Sendero Frédéric Nietzsche', de: 'Nietzsche-Wanderweg', it: 'Sentiero Frédéric Nietzsche', nl: 'Nietzsche Wandelpad', ru: 'Тропа Фридриха Ницше', ar: 'ممر فريدريك نيتشه', zh: '尼采徒步小径', ja: 'ニーチェ・ハイキングトレイル', ko: '니체 하이킹 코스', hi: 'नीत्शे हाइकिंग ट्रेल'
  },
  scanQR: {
    fr: 'Scannez pour accéder au site', en: 'Scan to access the site', es: 'Escanea para acceder al sitio', de: 'Scannen Sie für Website-Zugang', it: 'Scansiona per accedere al sito', nl: 'Scan voor toegang tot de site', ru: 'Сканируйте для доступа к сайту', ar: 'امسح للوصول إلى الموقع', zh: '扫描访问网站', ja: 'サイトにアクセスするにはスキャン', ko: '사이트 접속을 위해 스캔', hi: 'साइट तक पहुंचने के लिए स्कैन करें'
  },
  bycar: {
    fr: 'en voiture', en: 'by car', es: 'en coche', de: 'mit dem Auto', it: 'in auto', nl: 'met de auto', ru: 'на машине', ar: 'بالسيارة', zh: '开车', ja: '車で', ko: '차로', hi: 'कार से'
  },
  onfoot: {
    fr: 'à pied', en: 'on foot', es: 'a pie', de: 'zu Fuß', it: 'a piedi', nl: 'te voet', ru: 'пешком', ar: 'سيراً على الأقدام', zh: '步行', ja: '徒歩', ko: '도보', hi: 'पैदल'
  }
};

export function LocationSection({ language }) {
  const t = (key) => locationTranslations[key]?.[language] || locationTranslations[key]?.['en'] || key;
  
  // QR Code URL (using free QR code API)
  const siteUrl = 'https://cotedazur-stay.emergent.host';
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(siteUrl)}&bgcolor=ffffff&color=2d5016`;

  const distances = [
    { icon: Train, label: t('trainStation'), distance: '100 m', detail: `(2 min ${t('onfoot')})` },
    { icon: Bus, label: t('busStop'), distance: '800 m', detail: `(10 min ${t('onfoot')})` },
    { icon: Navigation, label: 'Monaco', distance: '8 km', detail: `(12 min ${t('bycar')})` },
    { icon: Navigation, label: 'Nice', distance: '12 km', detail: `(20 min ${t('bycar')})` },
    { icon: Mountain, label: t('nietzschePath'), distance: '500 m', detail: `(7 min ${t('onfoot')})` },
  ];

  return (
    <section id="location" className="py-16 md:py-20 px-4 md:px-8 lg:px-12 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight">{t('title')}</h2>
          <div className="w-20 h-1 bg-accent mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Address & QR Code */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-border text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="text-accent" size={24} />
              <h3 className="text-xl font-semibold">{t('address')}</h3>
            </div>
            <p className="text-lg mb-6">
              14 Chemin des Pins<br />
              06360 Eze, France
            </p>
            <div className="flex flex-col items-center">
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                className="w-32 h-32 mb-2 rounded-lg"
              />
              <p className="text-sm text-muted-foreground">{t('scanQR')}</p>
            </div>
          </div>

          {/* Distances */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-border">
            <h3 className="text-xl font-semibold mb-6 text-center lg:text-left">{t('distanceTo')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {distances.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <item.icon className="text-accent" size={24} />
                  </div>
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-accent">{item.distance}</span> {item.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Google Maps Embed */}
        <div className="mt-8 rounded-2xl overflow-hidden shadow-sm border border-border">
          <iframe
            title="Location Map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2884.7!2d7.3614!3d43.7284!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12cdc26f87ef2b7d%3A0x4f0e70b0e5c9e9e9!2s14%20Chemin%20des%20Pins%2C%2006360%20%C3%88ze%2C%20France!5e0!3m2!1sfr!2sfr!4v1640000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
